import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * Handles redirects from Supabase confirmation and recovery emails.
 *
 * Supabase encodes the session tokens in the URL hash fragment:
 *   #access_token=...&type=signup     (email confirmation)
 *   #access_token=...&type=recovery   (password reset)
 *
 * The Supabase JS client picks those up automatically via onAuthStateChange.
 * This page just waits for the auth state to resolve and then routes the user.
 *
 * Routing logic:
 *   - PASSWORD_RECOVERY event  → /reset-password?type=recovery
 *   - Authenticated, no profile or no company → /onboarding
 *   - Authenticated, has company              → /jobs
 *   - Not authenticated after timeout         → /login
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, profile } = useAuth();
  const [isRecovery, setIsRecovery] = useState(false);

  // Detect PASSWORD_RECOVERY event before auth context settles
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsRecovery(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (isLoading) return;

    if (isRecovery) {
      navigate("/reset-password?type=recovery", { replace: true });
      return;
    }

    if (isAuthenticated) {
      // A freshly-confirmed user has no profile row yet (profile === null).
      // A user who skipped onboarding has profile with company_id === null.
      // Both cases need onboarding.
      const hasCompany = profile !== null && profile.company_id !== null;
      if (hasCompany) {
        navigate("/jobs", { replace: true });
      } else {
        navigate("/onboarding", { replace: true });
      }
      return;
    }

    // Auth hasn't resolved yet — give Supabase time to process the hash tokens.
    // If still unauthenticated after 4 s the link is invalid or expired.
    const timeout = setTimeout(() => {
      navigate("/login", { replace: true });
    }, 4000);
    return () => clearTimeout(timeout);
  }, [isAuthenticated, isLoading, isRecovery, profile, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}
